import type {
	CourseMindItemSearchResult,
	MindItemSearchResult,
	ProductSearchResult,
	SearchResponseData,
	TeachingFormatListResponse,
	Topic,
	WorkloadListResponse,
} from '@kimuno/api-sdk';

import type { Pagination, SearchFilters } from '../types/search';
import type { AsyncAction, SyncAction } from './common.actionTypes';

export enum SEARCH_MIND_ITEMS {
	START = 'SEARCH_MIND_ITEMS_START',
	SUCCESS = 'SEARCH_MIND_ITEMS_SUCCESS',
	ERROR = 'SEARCH_MIND_ITEMS_ERROR',
}

export enum SEARCH_VIDEO_MIND_ITEMS {
	START = 'SEARCH_VIDEO_MIND_ITEMS_START',
	SUCCESS = 'SEARCH_VIDEO_MIND_ITEMS_SUCCESS',
	ERROR = 'SEARCH_VIDEO_MIND_ITEMS_ERROR',
}

export enum SEARCH_COURSE_MIND_ITEMS {
	START = 'SEARCH_COURSE_MIND_ITEMS_START',
	SUCCESS = 'SEARCH_COURSE_MIND_ITEMS_SUCCESS',
	ERROR = 'SEARCH_COURSE_MIND_ITEMS_ERROR',
}

export enum SEARCH_PRODUCTS {
	START = 'SEARCH_PRODUCTS_START',
	SUCCESS = 'SEARCH_PRODUCTS_SUCCESS',
	ERROR = 'SEARCH_PRODUCTS_ERROR',
}

export enum SEARCH_UNIFIED {
	START = 'SEARCH_UNIFIED_START',
	SUCCESS = 'SEARCH_UNIFIED_SUCCESS',
	ERROR = 'SEARCH_UNIFIED_ERROR',
}

export enum SEARCH_TOPICS {
	START = 'SEARCH_TOPICS_START',
	SUCCESS = 'SEARCH_TOPICS_SUCCESS',
	ERROR = 'SEARCH_TOPICS_ERROR',
}

export enum FETCH_WORKLOADS {
	START = 'FETCH_WORKLOADS_START',
	SUCCESS = 'FETCH_WORKLOADS_SUCCESS',
	ERROR = 'FETCH_WORKLOADS_ERROR',
}

export enum FETCH_TEACHING_FORMATS {
	START = 'FETCH_TEACHING_FORMATS_START',
	SUCCESS = 'FETCH_TEACHING_FORMATS_SUCCESS',
	ERROR = 'FETCH_TEACHING_FORMATS_ERROR',
}

export const SET_RESULTS_SEARCH_ID = 'SET_RESULTS_SEARCH_ID';

export const SEARCH_REQUEST_ABORTED = 'SEARCH_REQUEST_ABORTED';

export type SearchSlice =
	| 'mindItems'
	| 'videoMindItems'
	| 'courseMindItems'
	| 'products'
	| 'unified';

// `searchId` is the search journey the request belongs to, read from session storage at
// dispatch time. The store keeps it so a consumer can tell whether the results it is
// looking at belong to the journey that is current *now* — the search bar mints the next
// id before the request for it goes out, so agreement on filters alone cannot tell.
type SearchActionPayload = {
	filters: SearchFilters;
	pagination: Pagination;
	searchId?: string;
};

// `requestId` identifies the request a START marks pages for, so the ABORTED of a request
// that was replaced before it settled cannot touch the pages its replacement marked.
export type SearchStartPayload = SearchActionPayload & { requestId: number };

type SearchAsyncAction<Start extends string, Success extends string, Error extends string, Result> =
	| Extract<AsyncAction<Start, Success, Error, Result, SearchStartPayload>, { type: Start }>
	| Exclude<AsyncAction<Start, Success, Error, Result, SearchActionPayload>, { type: Start }>;

export type SearchMindItemsAction = SearchAsyncAction<
	SEARCH_MIND_ITEMS.START,
	SEARCH_MIND_ITEMS.SUCCESS,
	SEARCH_MIND_ITEMS.ERROR,
	SearchResponseData<MindItemSearchResult>
>;

export type SearchVideoMindItemsAction = SearchAsyncAction<
	SEARCH_VIDEO_MIND_ITEMS.START,
	SEARCH_VIDEO_MIND_ITEMS.SUCCESS,
	SEARCH_VIDEO_MIND_ITEMS.ERROR,
	SearchResponseData<MindItemSearchResult>
>;

export type SearchProductsAction = SearchAsyncAction<
	SEARCH_PRODUCTS.START,
	SEARCH_PRODUCTS.SUCCESS,
	SEARCH_PRODUCTS.ERROR,
	SearchResponseData<ProductSearchResult>
>;

export type SearchCourseMindItemsAction = SearchAsyncAction<
	SEARCH_COURSE_MIND_ITEMS.START,
	SEARCH_COURSE_MIND_ITEMS.SUCCESS,
	SEARCH_COURSE_MIND_ITEMS.ERROR,
	SearchResponseData<CourseMindItemSearchResult>
>;

export type SearchUnifiedAction = SearchAsyncAction<
	SEARCH_UNIFIED.START,
	SEARCH_UNIFIED.SUCCESS,
	SEARCH_UNIFIED.ERROR,
	SearchResponseData<MindItemSearchResult | ProductSearchResult>
>;

// A cancelled request settles nothing on its own, so the pages its START marked LOADING
// would stay that way; this is how the thunk hands the reducer what to drop. The
// pagination is a snapshot of the request's numbers — the thunk mutates the pagination
// object it holds, which is sometimes the store's own.
export type SearchRequestAbortedAction = SyncAction<
	typeof SEARCH_REQUEST_ABORTED,
	undefined,
	{ slice: SearchSlice; requestId: number; pagination: { offset: number; limit: number } }
>;

// Results fetched before any journey existed (a fresh tab opened straight on a search
// URL) carry no id, so SearchResults mints one when it reports on them and stamps it
// here — leaving it unset would give the next search no older id to disagree with.
export type SetResultsSearchIdAction = SyncAction<
	typeof SET_RESULTS_SEARCH_ID,
	undefined,
	{ searchId: string; isCourse: boolean }
>;

export type SearchTopicsAction = AsyncAction<
	SEARCH_TOPICS.START,
	SEARCH_TOPICS.SUCCESS,
	SEARCH_TOPICS.ERROR,
	Topic[],
	{ subject: string }
>;

export type FetchWorkloadsAction = AsyncAction<
	FETCH_WORKLOADS.START,
	FETCH_WORKLOADS.SUCCESS,
	FETCH_WORKLOADS.ERROR,
	WorkloadListResponse
>;

export type FetchTeachingFormatsAction = AsyncAction<
	FETCH_TEACHING_FORMATS.START,
	FETCH_TEACHING_FORMATS.SUCCESS,
	FETCH_TEACHING_FORMATS.ERROR,
	TeachingFormatListResponse
>;
